import React, { useState, useEffect, useMemo } from 'react';
import { fmtDec, MetricCard, REP_COLORS } from '../utils';
import { supabase } from '../../supabase';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const REP_NAMES = {
  peter: 'Peter Povey', mitch: 'Mitch Jankowski', dan: 'Dan Pena',
  sarah: 'Sarah Sutter', jared: 'Jared Gavard', justin: 'Justin Gavard',
  barry: 'Barry-Lynn', kelsey: 'Kelsey Gavard', unassigned: 'Unassigned',
};

function pct(n) {
  if (!isFinite(n)) return '—';
  return (Math.round(n * 10) / 10).toFixed(1) + '%';
}

function formatDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) { return str; }
}

function profitColor(n) {
  return n < 0 ? '#dc2626' : 'var(--text)';
}

function sumJobs(jobs) {
  const t = jobs.reduce((a, j) => {
    a.booked += j.total; a.collected += j.collected;
    a.expenses += j.expenses; a.net += j.net; a.jobCount++;
    return a;
  }, { booked: 0, collected: 0, expenses: 0, net: 0, jobCount: 0 });
  t.margin = t.collected > 0 ? (t.net / t.collected) * 100 : 0;
  return t;
}

function groupByRep(jobs) {
  const g = {};
  jobs.forEach(j => {
    const key = j.repId || 'unassigned';
    if (!g[key]) {
      g[key] = { repId: key, name: REP_NAMES[key] || j.owner || 'Unassigned',
                 jobCount: 0, booked: 0, collected: 0, expenses: 0, net: 0, jobs: [] };
    }
    const r = g[key];
    r.jobCount++; r.booked += j.total; r.collected += j.collected;
    r.expenses += j.expenses; r.net += j.net;
    r.jobs.push(j);
  });
  return Object.values(g).map(r => ({
    ...r,
    margin: r.collected > 0 ? (r.net / r.collected) * 100 : 0,
    jobs: r.jobs.sort((a, b) => b.net - a.net),
  })).sort((a, b) => b.net - a.net);
}

function exportCSV(jobs, byMonth, year) {
  const rows = [];
  rows.push(['JG Graphics - Profit']);
  rows.push([year + ' - paid jobs with expenses entered in Printavo']);
  rows.push([]);
  rows.push(['By month']);
  rows.push(['Month', 'Jobs', 'Collected', 'Expenses', 'Profit', 'Margin']);
  byMonth.forEach(m => {
    rows.push([MONTHS[m.month - 1], m.jobCount, m.collected.toFixed(2),
               m.expenses.toFixed(2), m.net.toFixed(2), pct(m.margin)]);
  });
  const yt = sumJobs(jobs);
  rows.push(['TOTAL', yt.jobCount, yt.collected.toFixed(2), yt.expenses.toFixed(2),
             yt.net.toFixed(2), pct(yt.margin)]);
  rows.push([]);

  rows.push(['By rep per month']);
  rows.push(['Month', 'Rep', 'Jobs', 'Collected', 'Expenses', 'Profit', 'Margin']);
  byMonth.forEach(m => {
    groupByRep(jobs.filter(j => j.month === m.month)).forEach(r => {
      rows.push([MONTHS[m.month - 1], r.name, r.jobCount, r.collected.toFixed(2),
                 r.expenses.toFixed(2), r.net.toFixed(2), pct(r.margin)]);
    });
  });
  rows.push([]);

  rows.push(['Job detail']);
  rows.push(['Month', 'Rep', 'Order #', 'Nickname', 'Customer', 'Date', 'Job total', 'Collected', 'Expenses', 'Profit', 'Margin']);
  jobs.slice().sort((a, b) => a.month - b.month || b.net - a.net).forEach(j => {
    rows.push([MONTHS[j.month - 1], REP_NAMES[j.repId] || j.owner, '#' + j.orderNumber,
               j.nickname, j.customer, formatDate(j.createdAt), j.total.toFixed(2),
               j.collected.toFixed(2), j.expenses.toFixed(2), j.net.toFixed(2), pct(j.margin)]);
  });
  rows.push([]);

  rows.push(['Expense detail']);
  rows.push(['Order #', 'Date', 'Category', 'Description', 'Amount', '% of job']);
  jobs.forEach(j => {
    j.expenseLines.forEach(e => {
      rows.push(['#' + j.orderNumber, e.date ? formatDate(e.date) : '', e.category, e.description,
                 e.amount.toFixed(2), j.total > 0 ? pct(e.amount / j.total * 100) : '']);
    });
  });

  const csv = rows.map(r => r.map(cell => {
    const s = String(cell == null ? '' : cell);
    return (s.includes(',') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(',')).join('\r\n');

  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'JG-Graphics-Profit-' + year + '.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Profit() {
  const [data, setData] = useState(null);        // meta from the last batch
  const [jobs, setJobs] = useState([]);          // accumulated across batches
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);   // orders scanned so far
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [month, setMonth] = useState(null);      // null = whole year
  const [openRep, setOpenRep] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [checks, setChecks] = useState([]);
  const [openCheck, setOpenCheck] = useState(null);

  // Weekly check log (written by the scheduled profit-check function).
  useEffect(() => {
    supabase
      .from('profit_checks')
      .select('id, ran_at, summary')
      .order('ran_at', { ascending: false })
      .limit(5)
      .then(({ data }) => { if (data) setChecks(data); })
      .catch(() => {});
  }, []);

  // Printavo can't be paged in one function call, so keep calling until done.
  const load = async (refresh) => {
    setLoading(true);
    setError(null);
    setRateLimited(false);
    setProgress(0);
    setJobs([]);

    const collected = [];
    const byId = new Set();
    let cursor = null;
    let first = true;

    try {
      for (let guard = 0; guard < 60; guard++) {
        const qs = first
          ? (refresh ? '?refresh=1' : '')
          : '?cursor=' + encodeURIComponent(cursor);
        first = false;

        const res = await fetch('/.netlify/functions/printavo-expenses' + qs);
        const d = await res.json();
        if (d.error) {
          setRateLimited(!!d.rateLimited);
          throw new Error(d.error);
        }

        (d.jobs || []).forEach(j => {
          if (byId.has(j.id)) return;
          byId.add(j.id);
          collected.push(j);
        });

        setJobs(collected.slice());
        setProgress(d.scanned || 0);
        setData(d);

        if (d.done || !d.nextCursor) break;
        cursor = d.nextCursor;
      }
      setLoading(false);
    } catch (e) {
      // Keep whatever came back before the failure.
      setJobs(collected.slice());
      if (collected.length) {
        setError(null);
        setLoading(false);
      } else {
        setError(e.message);
        setLoading(false);
      }
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  const allJobs = jobs;
  const shownJobs = useMemo(
    () => (month ? allJobs.filter(j => j.month === month) : allJobs),
    [allJobs, month]
  );
  const byRep  = useMemo(() => groupByRep(shownJobs), [shownJobs]);
  const totals = useMemo(() => sumJobs(shownJobs), [shownJobs]);
  const byMonth = useMemo(() => {
    const acc = {};
    allJobs.forEach(j => {
      if (!acc[j.month]) acc[j.month] = { month: j.month, jobCount: 0, booked: 0, collected: 0, expenses: 0, net: 0 };
      const a = acc[j.month];
      a.jobCount++; a.booked += j.total; a.collected += j.collected;
      a.expenses += j.expenses; a.net += j.net;
    });
    return Object.values(acc)
      .map(a => ({ ...a, margin: a.collected > 0 ? (a.net / a.collected) * 100 : 0 }))
      .sort((a, b) => a.month - b.month);
  }, [allJobs]);

  if (loading && !allJobs.length) {
    return (
      <div className="loading-screen" style={{ minHeight: 300 }}>
        <div className="spinner" />
        <span style={{ color: 'var(--text2)', fontSize: 13 }}>
          Scanning Printavo orders{progress ? ' — ' + progress.toLocaleString() + ' so far' : '...'}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="rep-welcome">
          <h2>Profit</h2>
          <p style={{ color: rateLimited ? 'var(--text2)' : '#dc2626' }}>
            {rateLimited ? error : 'Could not load Printavo data: ' + error}
          </p>
        </div>
        <button className="btn-primary" style={{ fontSize: 13, padding: '8px 16px' }}
                onClick={() => load(true)}>
          Try again
        </button>
      </div>
    );
  }

  const { year, awaiting, schemaMissing, schema, fetchedAt, cached, diag } = data || {};

  if (schemaMissing) {
    return (
      <div>
        <div className="rep-welcome">
          <h2>Profit</h2>
          <p>Connected to Printavo, but no expense field was found on the order schema.</p>
        </div>
        <div className="table-card" style={{ padding: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginTop: 0 }}>
            The tab is wired up and ready — it just needs to know where expenses live in the API.
            Open <code>/.netlify/functions/printavo-expenses?debug=schema</code> and send the output over.
          </p>
          {schema && schema.invoiceFields && (
            <details style={{ fontSize: 12, color: 'var(--text3)' }}>
              <summary style={{ cursor: 'pointer' }}>Fields Printavo does expose on an order</summary>
              <div style={{ marginTop: 8, lineHeight: 1.8 }}>{schema.invoiceFields.join(' · ')}</div>
            </details>
          )}
        </div>
      </div>
    );
  }

  const waiting = awaiting || { count: 0, collected: 0, byMonth: {} };
  const waitingShown = month
    ? (waiting.byMonth && waiting.byMonth[month]) || { count: 0, collected: 0 }
    : waiting;

  if (!allJobs.length) {
    return (
      <div>
        <div className="rep-welcome">
          <h2>Profit</h2>
          <p>No jobs qualify yet — a job shows up here once it has been paid and has expenses entered in Printavo.</p>
        </div>
        {waiting.count > 0 && (
          <div className="table-card" style={{ padding: 16, fontSize: 13, color: 'var(--text2)' }}>
            {waiting.count} paid {waiting.count === 1 ? 'job is' : 'jobs are'} waiting on expenses
            ({fmtDec(waiting.collected)} collected). They will appear automatically as expenses get added.
          </div>
        )}
      </div>
    );
  }

  const maxProfit    = Math.max(...byRep.map(r => Math.abs(r.net)), 1);
  const maxMonthNet  = Math.max(...byMonth.map(m => Math.abs(m.net)), 1);
  const label        = month ? MONTHS[month - 1] + ' ' + year : year;

  return (
    <div>
      <div className="rep-welcome">
        <h2>Profit</h2>
        <p>
          Collected payments minus job expenses from Printavo. Paid jobs with expenses entered ·
          by job date, from Jan 1 {year}
          {fetchedAt ? ' · updated ' + new Date(fetchedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
          {cached ? ' (cached)' : ''}
        </p>
        {diag && !loading && (
          <p style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>
            Scanned {(data.scanned || 0).toLocaleString()} orders · {diag.inYear} in {year} ·
            {' '}{diag.qualified} paid with expenses · {diag.noExpenses} paid without expenses ·
            {' '}{diag.unpaid} unpaid
          </p>
        )}
      </div>

      {/* month filter */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        <button onClick={() => { setMonth(null); setOpenRep(null); setOpenJob(null); }}
                style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (month === null ? 'var(--brand)' : 'rgba(0,0,0,0.12)'),
                  background: month === null ? 'var(--brand)' : '#fff',
                  color: month === null ? '#fff' : 'var(--text2)', fontWeight: month === null ? 600 : 400,
                }}>
          Full year
        </button>
        {byMonth.map(m => {
          const on = month === m.month;
          return (
            <button key={m.month}
                    onClick={() => { setMonth(on ? null : m.month); setOpenRep(null); setOpenJob(null); }}
                    style={{
                      fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                      border: '1px solid ' + (on ? 'var(--brand)' : 'rgba(0,0,0,0.12)'),
                      background: on ? 'var(--brand)' : '#fff',
                      color: on ? '#fff' : 'var(--text2)', fontWeight: on ? 600 : 400,
                    }}>
              {MONTHS[m.month - 1]}
            </button>
          );
        })}
      </div>

      <div className="metrics">
        <MetricCard label="Collected" value={fmtDec(totals.collected)} sub={totals.jobCount + ' jobs · ' + label} />
        <MetricCard label="Expenses" value={fmtDec(totals.expenses)}
                    sub={pct(totals.collected > 0 ? totals.expenses / totals.collected * 100 : 0) + ' of collected'} />
        <MetricCard label="Profit" value={fmtDec(totals.net)} valueStyle={{ color: profitColor(totals.net) }} sub="after expenses" />
        <MetricCard label="Margin" value={pct(totals.margin)} sub="blended" />
      </div>

      {loading && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
          padding: '9px 14px', fontSize: 12.5, color: '#92400e', marginBottom: 16,
        }}>
          Still scanning Printavo — {progress.toLocaleString()} orders checked,
          {' '}{allJobs.length} qualifying so far. Numbers below will keep climbing until it finishes.
        </div>
      )}

      {waitingShown.count > 0 && (
        <div style={{
          background: '#f3f6f8', border: '1px solid #dde5e9', borderRadius: 8,
          padding: '9px 14px', fontSize: 12.5, color: 'var(--text2)', marginBottom: 16,
        }}>
          {waitingShown.count} paid {waitingShown.count === 1 ? 'job has' : 'jobs have'} no expenses entered yet
          {month ? ' in ' + MONTHS[month - 1] : ''} ({fmtDec(waitingShown.collected)} collected) —
          excluded until they do, then added automatically.
        </div>
      )}

      {/* monthly breakdown */}
      <div className="table-card" style={{ marginBottom: 20 }}>
        <div className="table-header">
          <span className="table-header-title">By month</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary"
                    style={{ fontSize: 12, padding: '6px 12px', background: 'transparent',
                             color: 'var(--text2)', border: '1px solid rgba(0,0,0,0.12)' }}
                    onClick={() => load(true)}>
              Refresh
            </button>
            <button className="btn-primary" style={{ fontSize: 12, padding: '6px 12px' }}
                    onClick={() => exportCSV(allJobs, byMonth, year)}>
              Export CSV
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th className="td-num">Jobs</th>
                <th className="td-money">Collected</th>
                <th className="td-money">Expenses</th>
                <th className="td-money">Profit</th>
                <th className="td-num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {byMonth.map(m => {
                const on = month === m.month;
                return (
                  <tr key={m.month}
                      onClick={() => { setMonth(on ? null : m.month); setOpenRep(null); setOpenJob(null); }}
                      style={{ cursor: 'pointer', background: on ? '#eef7f8' : undefined,
                               fontWeight: on ? 600 : undefined }}>
                    <td>{MONTHS[m.month - 1]} {year}</td>
                    <td className="td-num">{m.jobCount}</td>
                    <td className="td-money">{fmtDec(m.collected)}</td>
                    <td className="td-money">{fmtDec(m.expenses)}</td>
                    <td className="td-money" style={{ color: profitColor(m.net), fontWeight: 600 }}>{fmtDec(m.net)}</td>
                    <td className="td-num">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        <div style={{ width: 54, height: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 3 }}>
                          <div style={{
                            width: Math.min(100, Math.abs(m.net) / maxMonthNet * 100) + '%',
                            height: '100%', background: 'var(--brand)', borderRadius: 3,
                          }} />
                        </div>
                        {pct(m.margin)}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(() => {
                const yt = sumJobs(allJobs);
                return (
                  <tr style={{ fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.08)' }}>
                    <td>Year to date</td>
                    <td className="td-num">{yt.jobCount}</td>
                    <td className="td-money">{fmtDec(yt.collected)}</td>
                    <td className="td-money">{fmtDec(yt.expenses)}</td>
                    <td className="td-money" style={{ color: profitColor(yt.net) }}>{fmtDec(yt.net)}</td>
                    <td className="td-num">{pct(yt.margin)}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* rep breakdown for the selected period */}
      <div className="table-card">
        <div className="table-header">
          <span className="table-header-title">Profit by rep — {label}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Rep</th>
                <th className="td-num">Jobs</th>
                <th className="td-money">Collected</th>
                <th className="td-money">Expenses</th>
                <th className="td-money">Profit</th>
                <th className="td-num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {byRep.map(r => {
                const color = REP_COLORS[r.repId] || 'var(--brand)';
                const isOpen = openRep === r.repId;
                return (
                  <React.Fragment key={r.repId}>
                    <tr onClick={() => { setOpenRep(isOpen ? null : r.repId); setOpenJob(null); }}
                        style={{ cursor: 'pointer', background: isOpen ? '#f9fafb' : undefined }}>
                      <td>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                          background: color, marginRight: 8,
                        }} />
                        {r.name}
                        <span style={{ color: 'var(--text3)', marginLeft: 6, fontSize: 11 }}>
                          {isOpen ? '▾' : '▸'}
                        </span>
                      </td>
                      <td className="td-num">{r.jobCount}</td>
                      <td className="td-money">{fmtDec(r.collected)}</td>
                      <td className="td-money">{fmtDec(r.expenses)}</td>
                      <td className="td-money" style={{ color: profitColor(r.net), fontWeight: 600 }}>{fmtDec(r.net)}</td>
                      <td className="td-num">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <div style={{ width: 54, height: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 3 }}>
                            <div style={{
                              width: Math.min(100, Math.abs(r.net) / maxProfit * 100) + '%',
                              height: '100%', background: color, borderRadius: 3,
                            }} />
                          </div>
                          {pct(r.margin)}
                        </div>
                      </td>
                    </tr>

                    {isOpen && r.jobs.map(j => (
                      <React.Fragment key={j.id}>
                        <tr onClick={(e) => { e.stopPropagation(); setOpenJob(openJob === j.id ? null : j.id); }}
                            style={{ cursor: 'pointer', fontSize: 12.5, background: '#fcfcfd' }}>
                          <td style={{ paddingLeft: 28 }}>
                            <span style={{ color: 'var(--text3)' }}>#{j.orderNumber}</span>{' '}
                            {j.nickname || j.customer}
                            <span style={{ color: 'var(--text3)', marginLeft: 6, fontSize: 11 }}>
                              {openJob === j.id ? '▾' : '▸'} {j.expenseLines.length}
                            </span>
                          </td>
                          <td className="td-num" style={{ color: 'var(--text3)' }}>{formatDate(j.createdAt)}</td>
                          <td className="td-money">{fmtDec(j.collected)}</td>
                          <td className="td-money">{fmtDec(j.expenses)}</td>
                          <td className="td-money" style={{ color: profitColor(j.net) }}>{fmtDec(j.net)}</td>
                          <td className="td-num">{pct(j.margin)}</td>
                        </tr>

                        {openJob === j.id && (
                          <>
                            {j.expenseLines.map((line, i) => (
                              <tr key={i} style={{ fontSize: 12, background: '#f7f8fa', color: 'var(--text2)' }}>
                                <td style={{ paddingLeft: 48 }}>
                                  <span style={{ fontWeight: 600 }}>{line.category}</span>
                                  {line.description ? <span style={{ color: 'var(--text3)' }}> · {line.description}</span> : null}
                                </td>
                                <td className="td-num" style={{ color: 'var(--text3)' }}>
                                  {line.date ? formatDate(line.date) : ''}
                                </td>
                                <td className="td-money" />
                                <td className="td-money" style={{ color: '#dc2626' }}>−{fmtDec(line.amount)}</td>
                                <td className="td-money" />
                                <td className="td-num" style={{ color: 'var(--text3)' }}>
                                  {j.total > 0 ? pct(line.amount / j.total * 100) + ' of job' : ''}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ fontSize: 12, background: '#f7f8fa', color: 'var(--text3)' }}>
                              <td style={{ paddingLeft: 48 }}>Job total invoiced</td>
                              <td />
                              <td className="td-money">{fmtDec(j.total)}</td>
                              <td className="td-money">
                                {j.total > 0 ? pct(j.expenses / j.total * 100) + ' expenses' : ''}
                              </td>
                              <td className="td-money" />
                              <td className="td-num" />
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}

              <tr style={{ fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.08)' }}>
                <td>Total</td>
                <td className="td-num">{totals.jobCount}</td>
                <td className="td-money">{fmtDec(totals.collected)}</td>
                <td className="td-money">{fmtDec(totals.expenses)}</td>
                <td className="td-money" style={{ color: profitColor(totals.net) }}>{fmtDec(totals.net)}</td>
                <td className="td-num">{pct(totals.margin)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <CheckLog checks={checks} openCheck={openCheck} setOpenCheck={setOpenCheck} />
    </div>
  );
}

// Last 5 scheduled checks, newest first.
function CheckLog({ checks, openCheck, setOpenCheck }) {
  if (!checks.length) return null;

  const stamp = iso => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch (e) { return iso; }
  };

  const line = (sum) => {
    if (!sum || sum.ok === false) return 'Check failed' + (sum && sum.error ? ' — ' + sum.error : '');
    const m = sum.missing || {}, w = sum.week || {};
    const who = (m.byRep || []).map(r => r.rep.split(' ')[0] + ' ' + r.count).join(', ');
    return `${m.count || 0} paid ${m.count === 1 ? 'job' : 'jobs'} missing expenses (${fmtDec(m.collected || 0)})`
      + (who ? ' — ' + who : '')
      + ` · week profit ${fmtDec(w.net || 0)} on ${w.count || 0} jobs (${pct(w.margin || 0)})`;
  };

  return (
    <div className="table-card" style={{ marginTop: 20 }}>
      <div className="table-header">
        <span className="table-header-title">Weekly check log</span>
        <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Saturdays · last 5 runs</span>
      </div>
      <div style={{ padding: '4px 0' }}>
        {checks.map(c => {
          const sum = c.summary || {};
          const open = openCheck === c.id;
          const jobs = (sum.missing && sum.missing.jobs) || [];
          return (
            <div key={c.id} style={{ borderTop: '1px solid rgba(0,0,0,0.05)', padding: '10px 16px' }}>
              <div onClick={() => setOpenCheck(open ? null : c.id)}
                   style={{ cursor: jobs.length ? 'pointer' : 'default', display: 'flex', gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 150, flexShrink: 0 }}>
                  {stamp(c.ran_at)}
                </span>
                <span style={{ fontSize: 12.5, color: sum.ok === false ? '#dc2626' : 'var(--text2)' }}>
                  {line(sum)}
                  {jobs.length ? <span style={{ color: 'var(--text3)' }}>{open ? '  ▾' : '  ▸'}</span> : null}
                </span>
              </div>
              {open && jobs.length > 0 && (
                <div style={{ marginTop: 8, marginLeft: 162 }}>
                  {jobs.map((j, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--text3)', padding: '2px 0' }}>
                      #{j.orderNumber} · {j.nickname} · {j.rep} · {fmtDec(j.collected)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
